import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Alert,
  AlertDescription,
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@sportscanner/ui";
import { adminUpdateProfileSchema } from "@sportscanner/schemas";
import { apiPatch, getApiErrorCode } from "@/lib/api";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import type { AdminRow } from "./admins-list";

interface Props {
  clubId: string;
  row: AdminRow;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditProfileDialog({
  clubId,
  row,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(row.firstName);
  const [lastName, setLastName] = useState(row.lastName);
  const [phone, setPhone] = useState(row.phone);
  const [email, setEmail] = useState(row.email);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const body: Record<string, string> = {};
      if (firstName.trim() !== row.firstName) body.firstName = firstName.trim();
      if (lastName.trim() !== row.lastName) body.lastName = lastName.trim();
      if (phone.trim() !== row.phone) body.phone = phone.trim();
      if (email.trim().toLowerCase() !== row.email.toLowerCase()) {
        body.email = email.trim();
      }
      const parsed = adminUpdateProfileSchema.safeParse(body);
      if (!parsed.success) {
        throw { error: parsed.error.issues[0]?.message ?? "VALIDATION_FAILED" };
      }
      return apiPatch(
        `/api/admin/clubs/${clubId}/admins/${row.id}`,
        parsed.data,
      );
    },
    onSuccess: () => {
      toast.success(t("admins.toast.profileSaved"));
      onSaved();
    },
    onError: (err) => {
      setErrorCode(getApiErrorCode(err));
    },
  });

  const emailChanged =
    email.trim().toLowerCase() !== row.email.toLowerCase() &&
    email.trim().length > 0;

  const noChanges =
    firstName.trim() === row.firstName &&
    lastName.trim() === row.lastName &&
    phone.trim() === row.phone &&
    !emailChanged;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (noChanges || mutation.isPending) return;
    setErrorCode(null);
    mutation.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(o) => {
        if (mutation.isPending) return;
        if (!o) onClose();
      }}
    >
      <ResponsiveDialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("admins.editProfile.title")}</DialogTitle>
          <DialogDescription>
            {t("admins.editProfile.description")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {errorCode && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t(`errors.api.${errorCode}`)}
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">
                {t("admins.editProfile.firstName")}
              </Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">
                {t("admins.editProfile.lastName")}
              </Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={mutation.isPending}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="phone">{t("admins.editProfile.phone")}</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={mutation.isPending}
              placeholder="+381..."
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">{t("admins.editProfile.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={mutation.isPending}
            />
            {emailChanged && (
              <p className="text-xs text-amber-600">
                {t("admins.editProfile.emailChangeWarning")}
              </p>
            )}
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={mutation.isPending}
              className="cursor-pointer"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={noChanges || mutation.isPending}
              className="cursor-pointer bg-primary-600 hover:bg-primary-700 text-white"
            >
              {mutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("common.save")
              )}
            </Button>
          </DialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}
